import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, ArrowLeft, Check, X, Globe, Video, HandCoins, Megaphone, LayoutGrid } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Reveal } from "./Reveal";
import { ArchWindow } from "./ArchWindow";
import { PORTFOLIO, FILTERS } from "../lib/portfolio";

const FILTER_ICONS = { All: LayoutGrid, Website: Globe, Livestream: Video, Giving: HandCoins, Marketing: Megaphone };

const FilterBar = ({ active, onChange, count }) => (
  <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="portfolio-filters">
    {FILTERS.map((f) => {
      const Icon = FILTER_ICONS[f];
      const on = active === f;
      return (
        <button
          key={f}
          type="button"
          onClick={() => onChange(f)}
          aria-pressed={on}
          data-testid={`portfolio-filter-${f.toLowerCase()}`}
          className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
            on ? "bg-[#E84C4C] border-[#E84C4C] text-white shadow-[0_8px_24px_rgba(232,76,76,0.3)]" : "border-[#120716]/15 text-[#120716]/60 hover:border-[#E84C4C]/60 hover:text-[#120716]"
          }`}
        >
          <Icon className="w-3.5 h-3.5" /> {f}
        </button>
      );
    })}
    <span className="ml-auto text-xs font-orbitron tracking-widest text-[#120716]/45" data-testid="portfolio-filter-count">
      {count} {count === 1 ? "CONCEPT" : "CONCEPTS"}
    </span>
  </div>
);

const ArchPhoto = ({ p, className = "", eager = false, children }) => (
  <div className={`arch-shape relative overflow-hidden bg-[#FFF5F2] ${className}`}>
    <img src={p.image} alt={`${p.name}, ${p.location}`} loading={eager ? "eager" : "lazy"} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: p.position }} />
    {children}
  </div>
);

const Card = ({ p, onOpen }) => (
  <button type="button" onClick={() => onOpen(p)} data-testid={`portfolio-card-${p.slug}`} className="group text-left w-full transition-transform duration-500 hover:-translate-y-2 focus:outline-none">
    <ArchPhoto p={p} className="w-full aspect-[3/4] shadow-[0_16px_44px_rgba(232,76,76,0.15)] group-hover:shadow-[0_24px_60px_rgba(232,76,76,0.3)] transition-shadow duration-500">
      <div className="absolute inset-0 bg-gradient-to-t from-[#120716]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 text-[9px] font-orbitron tracking-[0.25em] text-[#E84C4C] whitespace-nowrap">DEMO CONCEPT</span>
      <span className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-[10px] font-semibold text-[#120716] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 whitespace-nowrap">
        See the concept <ArrowUpRight className="w-3 h-3 text-[#E84C4C]" />
      </span>
    </ArchPhoto>
    <h3 className="font-orbitron font-semibold text-sm sm:text-base text-[#120716] mt-5 tracking-wide leading-snug">{p.name}</h3>
    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[#120716]/50"><MapPin className="w-3.5 h-3.5 text-[#E84C4C] shrink-0" /> {p.location}</p>
    <p className="mt-1.5 text-[10px] font-orbitron tracking-widest text-[#E84C4C]/70">{p.type}</p>
  </button>
);

const ConceptDialog = ({ p, onClose }) => (
  <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      className="w-[calc(100vw-1.25rem)] max-w-4xl max-h-[92dvh] overflow-y-auto bg-white border-[#120716]/10 p-0 rounded-2xl [&>button:last-of-type]:hidden"
      data-testid="portfolio-dialog"
    >
      {p && (
        <>
          <div className="sticky top-0 z-20 flex justify-end p-3 -mb-[3.75rem] pointer-events-none">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close and go back to all churches"
              data-testid="portfolio-dialog-close"
              className="pointer-events-auto w-11 h-11 rounded-full bg-white shadow-[0_6px_24px_rgba(18,7,22,0.2)] border border-[#120716]/10 flex items-center justify-center text-[#120716] hover:text-[#E84C4C] hover:border-[#E84C4C] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid md:grid-cols-[0.85fr_1.15fr]">
            <div className="p-6 pt-8 md:p-8 flex items-center justify-center bg-[#FFF5F2]">
              <ArchPhoto p={p} eager className="w-[190px] h-[254px] md:w-[240px] md:h-[320px] shadow-[0_16px_44px_rgba(232,76,76,0.25)]" />
            </div>
            <div className="p-6 md:p-10">
              <div className="flex items-center gap-3">
                <ArchWindow size={12} />
                <span className="font-orbitron text-[10px] tracking-[0.35em] text-[#E84C4C]">DEMO CONCEPT · {p.type.toUpperCase()}</span>
              </div>
              <DialogTitle className="font-orbitron font-bold text-xl md:text-2xl text-[#120716] mt-4 tracking-wide" data-testid="portfolio-dialog-title">{p.name}</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5 text-xs text-[#120716]/50 mt-1"><MapPin className="w-3.5 h-3.5 text-[#E84C4C]" /> {p.location}</DialogDescription>
              <div className="flex flex-wrap gap-2 mt-4" data-testid="portfolio-dialog-tags">
                {p.tags.map((t) => (
                  <span key={t} className="rounded-full bg-[#E84C4C]/10 border border-[#E84C4C]/25 text-[#E84C4C] text-[10px] font-semibold px-2.5 py-1">{t}</span>
                ))}
              </div>
              <p className="mt-5 text-sm md:text-base text-[#120716]/75 leading-relaxed" data-testid="portfolio-dialog-story">{p.story}</p>
              <p className="mt-6 text-[10px] font-orbitron tracking-widest text-[#E84C4C]/80">WHAT I'D BUILD</p>
              <ul className="mt-3 space-y-2">
                {p.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2.5 text-sm text-[#120716]/75"><Check className="w-4 h-4 text-[#E84C4C] mt-0.5 shrink-0" /> {d}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#apply" onClick={onClose} data-testid="portfolio-dialog-apply" className="text-center rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white text-sm font-semibold px-7 py-3 transition-all">
                  I want this for my church
                </a>
                <button type="button" onClick={onClose} data-testid="portfolio-dialog-back" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#120716]/15 text-[#120716]/70 hover:text-[#120716] hover:border-[#E84C4C]/60 text-sm font-semibold px-6 py-3 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back to all churches
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const Portfolio = () => {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.tags.includes(filter));
  return (
    <section id="portfolio" className="py-28 md:py-36 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3">
            <ArchWindow size={14} />
            <span className="font-orbitron text-xs tracking-[0.4em] text-[#E84C4C]">THE WORK</span>
          </div>
          <h2 className="font-orbitron font-bold text-2xl sm:text-3xl lg:text-4xl text-[#120716] mt-4 max-w-3xl leading-tight">
            TEN CHURCHES THAT MAKE ME WANT TO BUILD
          </h2>
          <p className="mt-6 max-w-2xl text-[#120716]/70 text-sm md:text-base leading-relaxed" data-testid="portfolio-intro">
            None of these are my clients. They are the most breathtaking churches on earth, and each window
            below is a demo concept: what I would design, build, and market if a congregation like this asked me.
            Open one and picture the same care applied to yours.
          </p>
          <p className="mt-4 max-w-2xl italic font-light text-[#C13B3B] text-sm md:text-base">
            "By their fruit you will recognize them." — Matthew 7:16
          </p>
          <FilterBar active={filter} onChange={setFilter} count={visible.length} />
        </Reveal>
        <motion.div layout className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12" data-testid="portfolio-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                key={p.slug}
                layout
                initial={{ opacity: 0, scale: 0.92, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card p={p} onOpen={setActive} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        <Reveal delay={0.2}>
          <p className="mt-14 text-center text-sm text-[#120716]/50 italic max-w-xl mx-auto">
            Your church doesn't need a famous facade to deserve this.{" "}
            <a href="#apply" className="text-[#E84C4C] font-semibold not-italic hover:underline" data-testid="portfolio-apply-link">Tell me about your church</a>
          </p>
          <p className="mt-6 text-center text-[10px] tracking-widest uppercase text-[#120716]/35">Demo concepts for illustration · Photos via Wikimedia Commons</p>
        </Reveal>
      </div>
      <ConceptDialog p={active} onClose={() => setActive(null)} />
    </section>
  );
};
